import { MODAL_NONE } from '@components/App/Modal/constants'
import { Group } from '@components/App/Toolbar'
import { Tooltip } from '@lib/components'
import { useIsMobile } from '@lib/hooks'
import { Button } from '@octopus-do/octopus-storybook'
import { Redo, Undo } from '@octopus-do/octopus-storybook/icons'
import {
  PROJECT_HISTORY_REDO_REQUEST,
  PROJECT_HISTORY_UNDO_REQUEST,
} from '@store/reducers/manager/middleware/history/types'
import type React from 'react'
import { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { useArtBoardDisabled } from '@/hooks/selectors/useArtBoardDisabled'
import type { AppState } from '@/types'

const History: React.FC = () => {
  const dispatch = useDispatch()
  const isMobile = useIsMobile()

  const isModalOpen = useSelector<AppState, boolean>(
    ({ ui }) => ui.modalType !== MODAL_NONE
  )

  const canUndo = useSelector<AppState, boolean>(({ ui }) => ui.undo)

  const canRedo = useSelector<AppState, boolean>(({ ui }) => ui.redo)

  const artboardDisabled = useArtBoardDisabled()

  const onUndo = useCallback(
    () =>
      canUndo &&
      !isModalOpen &&
      dispatch({ type: PROJECT_HISTORY_UNDO_REQUEST }),
    [canUndo, isModalOpen, dispatch]
  )
  const onRedo = useCallback(
    () =>
      canRedo &&
      !isModalOpen &&
      dispatch({ type: PROJECT_HISTORY_REDO_REQUEST }),
    [canRedo, isModalOpen, dispatch]
  )

  if ((!canUndo && !canRedo) || isMobile || artboardDisabled) {
    return null
  }

  return (
    <Group>
      <Tooltip content={canUndo ? 'Undo' : null} wrap={false} position="top">
        <Button
          priority="secondary"
          size="medium"
          left={Undo}
          aria-label="Undo"
          data-testid="undo"
          disabled={!canUndo}
          onClick={onUndo}
        />
      </Tooltip>

      <Tooltip content={canRedo ? 'Redo' : null} wrap={false} position="top">
        <Button
          priority="secondary"
          size="medium"
          left={Redo}
          aria-label="Redo"
          data-testid="redo"
          disabled={!canRedo}
          onClick={onRedo}
        />
      </Tooltip>
    </Group>
  )
}

export default History
